import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  marketplaceMOSBibleApi,
  type AICommandCapability,
  type AccessibilityStandard,
  type DailyWorkflowStep,
  type DashboardQuestion,
  type DesignSystemRule,
  type ExecutiveFocusArea,
  type HXOutcome,
  type InboxField,
  type MobileWorkflow,
  type MOSBibleStatus,
  type MOSDashboard,
  type NavItem,
  type NotificationRule,
  type PhilosophyQuestion,
  type ScreenInventoryEntry,
  type ScreenValidation,
  type SearchScope,
  type UserPsychologyFactor,
  type UXPrinciple,
  type WorkflowStandard,
  type WorkspaceSection,
} from '../../api/marketplace-mos-bible';

type Tab = 'overview' | 'philosophy' | 'navigation' | 'workspaces' | 'workflows' | 'design' | 'mobile' | 'screens' | 'status';
type Theme = 'light' | 'dark';

export function MarketplaceMOSBiblePage() {
  const [theme, setTheme] = useState<Theme>('light');
  const dark = theme === 'dark';
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<MOSDashboard | null>(null);
  const [philosophy, setPhilosophy] = useState<PhilosophyQuestion[]>([]);
  const [uxPrinciples, setUxPrinciples] = useState<UXPrinciple[]>([]);
  const [hxOutcomes, setHxOutcomes] = useState<HXOutcome[]>([]);
  const [dailyWorkflow, setDailyWorkflow] = useState<DailyWorkflowStep[]>([]);
  const [navigation, setNavigation] = useState<NavItem[]>([]);
  const [dashboardQs, setDashboardQs] = useState<DashboardQuestion[]>([]);
  const [aiCapabilities, setAiCapabilities] = useState<AICommandCapability[]>([]);
  const [inboxFields, setInboxFields] = useState<InboxField[]>([]);
  const [productSections, setProductSections] = useState<WorkspaceSection[]>([]);
  const [expSections, setExpSections] = useState<WorkspaceSection[]>([]);
  const [executiveAreas, setExecutiveAreas] = useState<ExecutiveFocusArea[]>([]);
  const [workflows, setWorkflows] = useState<WorkflowStandard[]>([]);
  const [notifications, setNotifications] = useState<NotificationRule[]>([]);
  const [searchScopes, setSearchScopes] = useState<SearchScope[]>([]);
  const [mobileWorkflows, setMobileWorkflows] = useState<MobileWorkflow[]>([]);
  const [accessibility, setAccessibility] = useState<AccessibilityStandard[]>([]);
  const [designSystem, setDesignSystem] = useState<DesignSystemRule[]>([]);
  const [psychology, setPsychology] = useState<UserPsychologyFactor[]>([]);
  const [screens, setScreens] = useState<ScreenInventoryEntry[]>([]);
  const [status, setStatus] = useState<MOSBibleStatus | null>(null);
  const [screenResult, setScreenResult] = useState<ScreenValidation | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, phil, ux, hx, daily, nav, dqs, ai, inbox, prod, exp, exec, wf, notif, search, mobile, a11y, ds, psych, scr, st] = await Promise.all([
        marketplaceMOSBibleApi.dashboard(),
        marketplaceMOSBibleApi.philosophy(),
        marketplaceMOSBibleApi.uxPrinciples(),
        marketplaceMOSBibleApi.hxOutcomes(),
        marketplaceMOSBibleApi.dailyWorkflow(),
        marketplaceMOSBibleApi.navigation(),
        marketplaceMOSBibleApi.dashboardQuestions(),
        marketplaceMOSBibleApi.aiCommandCenter(),
        marketplaceMOSBibleApi.opportunityInbox(),
        marketplaceMOSBibleApi.productWorkspace(),
        marketplaceMOSBibleApi.experimentWorkspace(),
        marketplaceMOSBibleApi.executiveWorkspace(),
        marketplaceMOSBibleApi.workflows(),
        marketplaceMOSBibleApi.notificationRules(),
        marketplaceMOSBibleApi.searchScopes(),
        marketplaceMOSBibleApi.mobileWorkflows(),
        marketplaceMOSBibleApi.accessibility(),
        marketplaceMOSBibleApi.designSystem(),
        marketplaceMOSBibleApi.userPsychology(),
        marketplaceMOSBibleApi.screenInventory(),
        marketplaceMOSBibleApi.status(),
      ]);
      setDashboard(dash);
      setPhilosophy(phil);
      setUxPrinciples(ux);
      setHxOutcomes(hx);
      setDailyWorkflow(daily);
      setNavigation(nav);
      setDashboardQs(dqs);
      setAiCapabilities(ai);
      setInboxFields(inbox);
      setProductSections(prod);
      setExpSections(exp);
      setExecutiveAreas(exec);
      setWorkflows(wf);
      setNotifications(notif);
      setSearchScopes(search);
      setMobileWorkflows(mobile);
      setAccessibility(a11y);
      setDesignSystem(ds);
      setPsychology(psych);
      setScreens(scr);
      setStatus(st);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load MOS bible');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const validateScreen = async () => {
    setBusy(true);
    setActionMsg(null);
    try {
      const result = await marketplaceMOSBibleApi.validateScreen({
        screen_key: 'universal_dashboard',
        attrs: { guides_action: true, reduces_complexity: true, shows_confidence: true, measurable_outcome: true },
      });
      setScreenResult(result);
      setActionMsg(result.valid ? 'Screen passes MIB-000005 UX standards' : `Gaps: ${result.gaps?.join(', ')}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Validation failed');
    } finally {
      setBusy(false);
    }
  };

  const card = dark ? 'border-slate-800 bg-slate-900' : 'border-slate-200 bg-white';
  const muted = dark ? 'text-slate-400' : 'text-slate-500';
  const shell = dark ? 'bg-slate-950 text-slate-100' : 'bg-white text-slate-900';
  const accent = 'text-emerald-500';
  const tabActive = 'bg-emerald-600 text-white';

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Executive Summary' },
    { id: 'philosophy', label: 'Philosophy & HX' },
    { id: 'navigation', label: 'Navigation & Daily OS' },
    { id: 'workspaces', label: 'Workspaces' },
    { id: 'workflows', label: 'Workflows & Notifications' },
    { id: 'design', label: 'Design System' },
    { id: 'mobile', label: 'Mobile & Search' },
    { id: 'screens', label: 'Screen Inventory' },
    { id: 'status', label: 'Bible Status' },
  ];

  return (
    <div className={`space-y-6 rounded-xl p-1 ${shell}`}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className={`text-xs font-semibold uppercase tracking-wide ${accent}`}>MIB-000005</p>
          <h1 className="text-2xl font-semibold">Marketplace Operating System Bible</h1>
          <p className={`text-sm ${muted}`}>
            World-class UX, human experience, decision experience, daily workflow and marketplace control center
          </p>
          <div className="mt-2 flex flex-wrap gap-2 text-xs">
            <Link to="/platform/marketplace-intelligence-bible" className={`${accent} hover:underline`}>MIB-000001</Link>
            <span className={muted}>·</span>
            <Link to="/platform/marketplace-kpi-bible" className={`${accent} hover:underline`}>MIB-000002</Link>
            <span className={muted}>·</span>
            <Link to="/platform/marketplace-dna-bible" className={`${accent} hover:underline`}>MIB-000003</Link>
            <span className={muted}>·</span>
            <Link to="/platform/marketplace-growth-experiment-bible" className={`${accent} hover:underline`}>MIB-000004</Link>
          </div>
        </div>
        <button type="button" onClick={() => setTheme(dark ? 'light' : 'dark')} className={`rounded-lg border px-3 py-1.5 text-sm ${card}`}>
          {dark ? 'Light mode' : 'Dark mode'}
        </button>
      </div>

      {error && <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">{error}</div>}
      {actionMsg && <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">{actionMsg}</div>}

      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-2 dark:border-slate-800">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)} className={`rounded-lg px-3 py-1.5 text-sm ${tab === t.id ? tabActive : `${card} ${muted}`}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && dashboard && (
        <div className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'Bible ID', value: dashboard.bible_id },
              { label: 'Navigation Items', value: dashboard.navigation_items },
              { label: 'Daily Workflow Steps', value: dashboard.daily_workflow_steps },
              { label: 'Screen Inventory', value: dashboard.screen_inventory },
              { label: 'Workflow Standards', value: dashboard.workflow_standards },
              { label: 'Design System Rules', value: dashboard.design_system_rules },
              { label: 'UX Principles', value: dashboard.ux_principles },
              { label: 'Accessibility Standards', value: dashboard.accessibility_standards },
            ].map((item) => (
              <div key={item.label} className={`rounded-xl border p-4 ${card}`}>
                <p className={`text-xs uppercase ${muted}`}>{item.label}</p>
                <p className="mt-1 text-2xl font-semibold">{item.value}</p>
              </div>
            ))}
          </div>
          <div className={`rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">Universal Dashboard Questions</h3>
            <ol className="mt-3 list-decimal space-y-1 pl-5 text-sm">
              {dashboardQs.map((q) => <li key={q.key}>{q.question}</li>)}
            </ol>
          </div>
          <div className={`rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">AI Growth Partner — Not ERP</h3>
            <p className={`mt-2 text-sm ${muted}`}>Every screen helps sellers make better business decisions faster. Not display information — drive measurable improvement.</p>
          </div>
        </div>
      )}

      {tab === 'philosophy' && (
        <div className="grid gap-4 lg:grid-cols-2">
          <div className={`rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">Users Always Know</h3>
            <ol className="mt-3 list-decimal space-y-2 pl-5">
              {philosophy.map((q) => <li key={q.key} className="font-medium">{q.question}</li>)}
            </ol>
          </div>
          <div className={`rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">Human Experience Outcomes</h3>
            <div className="mt-3 flex flex-wrap gap-2">
              {hxOutcomes.map((o) => (
                <span key={o.key} className="rounded-full bg-emerald-100 px-3 py-1 text-sm dark:bg-emerald-900" title={o.description}>{o.name}</span>
              ))}
            </div>
          </div>
          <div className={`col-span-full rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">UX Principles</h3>
            <div className="mt-3 grid gap-2 md:grid-cols-2">
              {uxPrinciples.map((p) => (
                <div key={p.key} className="rounded-lg border p-3 dark:border-slate-700">
                  <div className="flex items-center gap-2">
                    <span className={`rounded px-2 py-0.5 text-xs ${p.category === 'reduce' ? 'bg-amber-100 dark:bg-amber-900' : 'bg-emerald-100 dark:bg-emerald-900'}`}>{p.category}</span>
                    <span className="font-medium">{p.name}</span>
                  </div>
                  <p className={`mt-1 text-sm ${muted}`}>{p.description}</p>
                </div>
              ))}
            </div>
          </div>
          <div className={`col-span-full rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">User Psychology</h3>
            <div className="mt-3 grid gap-2 md:grid-cols-3">
              {psychology.map((f) => (
                <div key={f.key} className="rounded-lg border p-3 dark:border-slate-700">
                  <span className="font-medium">{f.name}</span>
                  <p className={`mt-1 text-sm ${muted}`}>{f.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'navigation' && (
        <div className="space-y-4">
          <div className={`rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">Universal Navigation Architecture</h3>
            <div className="mt-3 grid gap-2 md:grid-cols-2 lg:grid-cols-3">
              {navigation.map((n) => (
                <div key={n.key} className="flex items-center justify-between rounded-lg border p-3 dark:border-slate-700">
                  <div>
                    <span className="font-medium">{n.label}</span>
                    <span className={`ml-2 text-xs ${muted}`}>{n.group}</span>
                  </div>
                  {n.quick_action && <span className="rounded bg-emerald-100 px-2 py-0.5 text-xs dark:bg-emerald-900">quick</span>}
                </div>
              ))}
            </div>
          </div>
          <div className={`rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">Daily Operating System</h3>
            <div className="mt-3 grid gap-2 md:grid-cols-2">
              {dailyWorkflow.map((s) => (
                <div key={s.key} className="rounded-lg border p-3 dark:border-slate-700">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-emerald-500">{s.order}</span>
                    <span className="font-medium">{s.name}</span>
                    <span className={`rounded px-2 py-0.5 text-xs ${muted}`}>{s.phase}</span>
                  </div>
                  {s.description && <p className={`mt-1 text-sm ${muted}`}>{s.description}</p>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'workspaces' && (
        <div className="grid gap-4 lg:grid-cols-2">
          <div className={`rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">ASIN Product Workspace</h3>
            <div className="mt-3 space-y-1">
              {productSections.map((s) => (
                <div key={s.key} className="flex items-center gap-2 rounded border p-2 dark:border-slate-700">
                  <span className="font-mono text-xs text-emerald-500">{s.order}</span>
                  <span>{s.name}</span>
                </div>
              ))}
            </div>
          </div>
          <div className={`rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">Experiment Workspace</h3>
            <div className="mt-3 space-y-1">
              {expSections.map((s) => (
                <div key={s.key} className="flex items-center gap-2 rounded border p-2 dark:border-slate-700">
                  <span className="font-mono text-xs text-emerald-500">{s.order}</span>
                  <span>{s.name}</span>
                </div>
              ))}
            </div>
          </div>
          <div className={`rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">AI Command Center</h3>
            <div className="mt-3 space-y-2">
              {aiCapabilities.map((c) => (
                <div key={c.key} className="rounded-lg border p-3 dark:border-slate-700">
                  <span className="font-medium">{c.name}</span>
                  <p className={`text-sm ${muted}`}>{c.description}</p>
                </div>
              ))}
            </div>
          </div>
          <div className={`rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">Opportunity Inbox Fields</h3>
            <div className="mt-3 space-y-2">
              {inboxFields.map((f) => (
                <div key={f.key} className="flex items-center justify-between rounded border p-2 dark:border-slate-700">
                  <span>{f.name}</span>
                  {f.required && <span className="text-xs text-red-500">required</span>}
                </div>
              ))}
            </div>
          </div>
          <div className={`col-span-full rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">Executive Workspace</h3>
            <div className="mt-3 flex flex-wrap gap-2">
              {executiveAreas.map((a) => (
                <span key={a.key} className={`rounded-full px-3 py-1 text-sm ${
                  a.category === 'metric' ? 'bg-blue-100 dark:bg-blue-900' :
                  a.category === 'opportunity' ? 'bg-emerald-100 dark:bg-emerald-900' :
                  a.category === 'risk' ? 'bg-red-100 dark:bg-red-900' : 'bg-violet-100 dark:bg-violet-900'
                }`}>{a.name}</span>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'workflows' && (
        <div className="grid gap-4 lg:grid-cols-2">
          <div className={`rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">Workflow Standards</h3>
            <div className="mt-3 space-y-3">
              {workflows.map((w) => (
                <div key={w.key} className="rounded-lg border p-4 dark:border-slate-700">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{w.name}</span>
                    {w.max_clicks != null && <span className={`text-xs ${muted}`}>≤ {w.max_clicks} clicks</span>}
                  </div>
                  <p className={`mt-1 text-sm ${muted}`}>{w.description}</p>
                  <ol className="mt-2 list-decimal pl-5 text-sm">
                    {w.steps.map((step) => <li key={step}>{step}</li>)}
                  </ol>
                </div>
              ))}
            </div>
          </div>
          <div className={`rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">Notification Philosophy</h3>
            <div className="mt-3 space-y-2">
              {notifications.map((r) => (
                <div key={r.key} className="rounded-lg border p-3 dark:border-slate-700">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{r.name}</span>
                    <span className={`text-xs ${r.noise_level === 'silent' ? muted : 'text-amber-500'}`}>{r.noise_level}</span>
                  </div>
                  <p className={`text-sm ${muted}`}>{r.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'design' && (
        <div className="grid gap-4 lg:grid-cols-2">
          <div className={`rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">Design System Rules</h3>
            <div className="mt-3 grid gap-2">
              {designSystem.map((r) => (
                <div key={r.key} className="rounded-lg border p-3 dark:border-slate-700">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{r.name}</span>
                    <span className={`text-xs ${muted}`}>{r.category}</span>
                  </div>
                  <p className={`text-sm ${muted}`}>{r.description}</p>
                </div>
              ))}
            </div>
          </div>
          <div className={`rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">Accessibility Standards</h3>
            <div className="mt-3 space-y-2">
              {accessibility.map((a) => (
                <div key={a.key} className="rounded-lg border p-3 dark:border-slate-700">
                  <span className="font-medium">{a.name}</span>
                  {a.standard && <span className={`ml-2 text-xs ${muted}`}>{a.standard}</span>}
                  <p className={`text-sm ${muted}`}>{a.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'mobile' && (
        <div className="grid gap-4 lg:grid-cols-2">
          <div className={`rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">Mobile-First Workflows</h3>
            <div className="mt-3 space-y-2">
              {mobileWorkflows.map((m) => (
                <div key={m.key} className="flex items-center justify-between rounded-lg border p-3 dark:border-slate-700">
                  <div>
                    <span className="font-medium">{m.name}</span>
                    <p className={`text-sm ${muted}`}>{m.description}</p>
                  </div>
                  <span className={`rounded px-2 py-0.5 text-xs ${m.priority === 'critical' ? 'bg-red-100 dark:bg-red-900' : 'bg-emerald-100 dark:bg-emerald-900'}`}>{m.priority}</span>
                </div>
              ))}
            </div>
          </div>
          <div className={`rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">Universal Search & Command Palette</h3>
            <div className="mt-3 space-y-2">
              {searchScopes.map((s) => (
                <div key={s.key} className="flex items-center justify-between rounded border p-2 dark:border-slate-700">
                  <span>{s.name}</span>
                  {s.command_palette && <span className="text-xs text-emerald-500">⌘ palette</span>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'screens' && (
        <div className="space-y-4">
          <div className={`rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">Screen Inventory</h3>
            <div className="mt-3 grid gap-3 md:grid-cols-2">
              {screens.map((s) => (
                <div key={s.key} className="rounded-lg border p-4 dark:border-slate-700">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{s.name}</span>
                    <span className={`text-xs ${muted}`}>{s.workspace}</span>
                  </div>
                  <p className={`mt-1 text-sm ${muted}`}>{s.purpose}</p>
                  {s.primary_action && <p className="mt-2 text-sm text-emerald-500">→ {s.primary_action}</p>}
                </div>
              ))}
            </div>
          </div>
          <div className={`rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">Validate Screen Against MOS Standards</h3>
            <button type="button" disabled={busy} onClick={validateScreen} className="mt-3 rounded-lg bg-emerald-600 px-4 py-2 text-sm text-white disabled:opacity-50">
              {busy ? 'Validating…' : 'Validate Universal Dashboard'}
            </button>
            {screenResult && (
              <div className="mt-4 grid gap-2 md:grid-cols-4">
                {[
                  { label: 'Valid', value: screenResult.valid ? 'Yes' : 'No' },
                  { label: 'Guides Action', value: screenResult.guides_action ? 'Yes' : 'No' },
                  { label: 'Reduces Complexity', value: screenResult.reduces_complexity ? 'Yes' : 'No' },
                  { label: 'Increases Confidence', value: screenResult.increases_confidence ? 'Yes' : 'No' },
                ].map((item) => (
                  <div key={item.label} className="rounded-lg border p-3 dark:border-slate-700">
                    <p className={`text-xs ${muted}`}>{item.label}</p>
                    <p className="font-semibold">{item.value}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {tab === 'status' && status && (
        <div className={`rounded-xl border p-4 ${card}`}>
          <h3 className="font-medium">MIB-000005 Bible Status</h3>
          <dl className="mt-4 grid gap-3 md:grid-cols-2">
            {Object.entries(status).map(([k, v]) => (
              <div key={k}>
                <dt className={`text-xs uppercase ${muted}`}>{k.replace(/_/g, ' ')}</dt>
                <dd className="font-medium">{Array.isArray(v) ? v.join(', ') : String(v)}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}
    </div>
  );
}
